<script setup lang="ts">
import { computed } from "vue";
import { LIFECYCLE_CHIP_GROUPS, stageDisplayLabel, stageFullName } from "@/features/BuildingProject/utils/lifecycleStages";

/**
 * The life cycle a calculation type assesses, small enough for a dialog and read-only by default.
 *
 * Shows the WHOLE life cycle rather than only what is included, because that is the part that teaches:
 * someone who does not know LCA can see that A4 is transport to site, that B1-B7 is the building in
 * use, and that this calculation ignores most of it. A list of the included codes alone says nothing —
 * "A1A3" is not a word.
 *
 * Was a compact cousin of IncludedPhases on the Calculation prerequisites step (943px wide, 278px tall,
 * always editable, dimmed to 50% opacity when disabled). That step now renders THIS component instead
 * (RTLCA create-wizard audit, finding F1), in its `editable` mode below — so the two are no longer two
 * descriptions of the life cycle, they are one, and the two surfaces cannot disagree about what B6 is
 * called or how a stage groups with its neighbours.
 *
 * Renders whatever it is given, including an empty set, which comes out as an all-grey life cycle. It
 * does NOT explain an empty set: what that means depends on WHY it is empty, and only the caller knows
 * that. A Custom calculation type is empty because the user has not chosen yet; the strip saying
 * otherwise was how it came to claim the wizard would "ask only for the project's own details", which
 * is the opposite of what Custom does.
 *
 * `editable` is opt-in and defaults to false, so every existing caller (the start-project dialog) keeps
 * the plain, non-interactive chips — nothing here changes their rendering path.
 * Calculation prerequisites passes `editable` only for the Custom calculation type; every other type
 * renders through the SAME false-by-default branch as the dialog, which is why it needs no separate
 * "locked" treatment of its own — it already looks exactly like the read-only case everywhere else in
 * the app, rather than like a control someone forgot to enable.
 */
/**
 * `size` scales the whole strip to its surface: "sm" (default) is the compact fit for the 640px
 * start dialog — sized so the WHOLE cycle fits its content width without the scroll fallback below,
 * because a dialog that asks the reader to swipe to find the stages it requires (the Custom
 * calculation type's editable strip, RTLCA-5595) is asking them to find a required field; "md" is
 * for the wizard's Calculation card, where the same strip at dialog size sat visibly too small in
 * the room it has (RTLCA-5421 follow-up). Same shape, same behavior — only typography, padding and
 * gaps grow.
 */
const props = withDefaults(defineProps<{ includedStages: string[]; editable?: boolean; size?: "sm" | "md" }>(), {
  editable: false,
  size: "sm",
});

const emit = defineEmits<{ toggleStage: [value: string] }>();

const groups = LIFECYCLE_CHIP_GROUPS;

const isIncluded = (value: string) => props.includedStages.includes(value);

/**
 * The groups render as COLUMNS, side by side in life-cycle order — the EN 15978 / ISO "Figure 6"
 * shape (span over a named group header over the modules) that the standard's own diagrams use and
 * our module displays mirrored before the redesign flattened this strip into rows (RTLCA-5421). The
 * recognizability is the point: someone who knows the standard should see the standard.
 *
 * A GRID rather than a wrapping flex row, so the life cycle reads as one continuous row. A flex row
 * wrapping put "Beyond System Boundary" on a second line of its own in the start dialog — the one group
 * that is already set apart by being outlined, dropped below the cycle it is supposed to sit beside,
 * reading as an afterthought rather than as the standard's supplementary column. Three explicit rows
 * shared by every column (span / header / modules) are what a flex column could not give: every group's
 * chips sit under its own header without affecting its neighbours'. A container too narrow for the
 * whole cycle scrolls horizontally rather than clipping or breaking the shape (see `columnTemplate`
 * below for how wide each column is allowed to get before that happens).
 */
const groupIncluded = (group: (typeof groups)[number]) => group.values.some(isIncluded);

/**
 * fills a group that isn't full then clear ones that isnt
 */
const toggleGroup = (group: (typeof groups)[number]) => {
  const include = !group.values.every(isIncluded);
  group.values.filter((value) => isIncluded(value) !== include).forEach((value) => emit("toggleStage", value));
};

/**
 * One grid column per group, WEIGHTED by how many modules it holds — Use (7 modules) claims roughly
 * seven times the extra width that Beyond System Boundary (1 module) does, matching the standard's own
 * proportions instead of splitting leftover space evenly or leaving it as dead gap between columns.
 *
 * `minmax(max-content, Nfr)` is the load-bearing part: the `max-content` floor means a column never
 * shrinks below what its own header text or chip row needs on ONE line — so nothing wraps or gets
 * clipped — while the `Nfr` ceiling lets it grow past that floor to soak up leftover width in proportion
 * to its weight once the card is wider than the strip's natural size. When even the floors don't fit,
 * the grid overflows its track sizes and the wrapper's `overflow-x-auto` below takes over, exactly the
 * scroll fallback this component has always relied on for a container too narrow for the whole cycle.
 */
const columnTemplate = computed(() => groups.map((group) => `minmax(max-content, ${group.values.length}fr)`).join(" "));

// D is outlined rather than filled, in every state: the standard draws "beyond the system boundary"
// as supplementary information outside the building life cycle, and the outline is how our module
// displays have always carried that distinction.
const groupHeaderClass = (group: (typeof groups)[number]) => {
  const sizing = props.size === "md" ? "t-caption-bold px-2.5 py-1" : "t-footnote-bold px-1.5 py-0.5";
  if (group.span === "D") {
    return groupIncluded(group)
      ? `${sizing} ring-app-blue-600 text-app-blue-800 ring-1`
      : `${sizing} ring-slate-200 text-type-muted ring-1`;
  }
  return groupIncluded(group)
    ? `${sizing} bg-app-blue-600 dark:bg-app-blue-fill text-white`
    : `${sizing} bg-slate-100 text-type-muted`;
};

// What assistive tech hears for one chip: the module's full name and whether this calculation
// assesses it. The bare code carries no meaning to a screen reader, and inclusion is not encoded in
// hue alone, so every chip states the verdict outright.
const stageAnnouncement = (value: string) => `${stageFullName(value)}, ${isIncluded(value) ? "assessed" : "not assessed"}`;

const chipClass = (value: string) => [
  "rounded-4 tabular-nums",
  props.size === "md" ? "px-2 py-1" : "px-1 py-0.5",
  isIncluded(value)
    ? `${props.size === "md" ? "t-small-bold" : "t-caption-bold"} bg-app-blue-50 text-app-blue-900`
    : `${props.size === "md" ? "t-small" : "t-caption"} text-type-muted bg-slate-100`,
];

// Editable-only: a hover cue so a real button reads as clickable, on top of the same colors `chipClass`
// gives the read-only chip.
const chipHoverClass = (value: string) => (isIncluded(value) ? "hover:bg-app-blue-100" : "hover:bg-slate-200");
</script>

<template>
  <!-- Scrolls rather than clips when the container cannot hold the whole cycle. The shape is the point,
       so breaking it up is worse than asking for a swipe.

       `p-px-2`: `overflow-x-auto` clips PAINT, not just layout — the D group's `ring-1` (and an
       editable chip's `focus-ring`) is a box-shadow that extends past its own box, and the proportional
       columns above now routinely size the last group flush against this wrapper's edge with nothing
       left over for that shadow to render into. Without this padding that ring's outer edge is cropped
       off silently: still there in the DOM, invisible on screen, and `scrollWidth === clientWidth`
       reports no overflow at all, since box-shadow never counts toward scrollable overflow. -->
  <div class="p-px-2 w-full overflow-x-auto">
    <div
      class="grid grid-flow-col grid-rows-[auto_auto_auto]"
      :class="size === 'md' ? 'gap-x-4 gap-y-1.5' : 'gap-x-2 gap-y-1'"
      :style="{ gridTemplateColumns: columnTemplate }">
      <!-- One column per group in life-cycle order: span, header, modules — the standard's Figure 6
           shape (see groupIncluded above). A bare template, so the three land in the column's own three
           rows; a wrapper element would be a single item and take the shared rows with it. -->
      <template
        v-for="group in groups"
        :key="group.headline">
        <span
          class="text-type-muted self-end text-center"
          :class="size === 'md' ? 't-caption' : 't-footnote'"
          >{{ group.span }}</span
        >
        <!-- A band across the whole group: grid items stretch by default, so the pill takes its column's
             full width and its modules sit under a header that covers them, rather than under a pill
             floating in the middle. It stretches to the header ROW's height too, so every pill is the
             same size whether its own headline wrapped or not.

             "md" (the wizard's roomy Calculation card) keeps the text on ONE line: uncapped, its
             single-line width is what `columnTemplate` sizes THAT column from, so the group grows its
             own column wide enough for its name instead of shrinking its name to fit a column sized for
             something else. "sm" (the 640px start dialog) keeps the ORIGINAL wrap-and-cap instead —
             that size has no room to spare (see the component doc above: it's sized to fit with no
             scroll fallback), and an uncapped "Beyond System Boundary" there overflows it by a few
             pixels, forcing the one scroll this size was specifically built to avoid on a REQUIRED
             field. -->
        <!-- the band is a toggle for the whole group, so the seven B modules go in or out in
             one click instead of seven. -->
        <button
          v-if="editable"
          type="button"
          :aria-pressed="group.values.every(isIncluded)"
          :aria-label="`${group.headline}, ${group.span}, all modules`"
          class="rounded-4 focus-ring interactive-active duration-fast ease-standard flex items-center justify-center text-center transition-colors"
          :class="groupHeaderClass(group)"
          @click="toggleGroup(group)">
          <span :class="size === 'md' ? 'whitespace-nowrap' : 'max-w-20'">{{ group.headline }}</span>
        </button>

        <span
          v-else
          class="rounded-4 flex items-center justify-center text-center"
          :class="groupHeaderClass(group)">
          <span :class="size === 'md' ? 'whitespace-nowrap' : 'max-w-20'">{{ group.headline }}</span>
        </span>

        <div
          class="flex justify-center"
          :class="size === 'md' ? 'gap-1.5' : 'gap-0.5'">
          <template
            v-for="value in group.values"
            :key="value">
            <!-- Editable: a real toggle button, so the Custom calculation type is edited here instead of
               on a second, disagreeing control. Production's three A1/A2/A3 labels already collapsed to
               this ONE chip on `value` (see lifecycleStages.ts), which is what keeps them toggling as one
               unit without any extra "grouped stage" logic here. -->
            <!-- The sentence lives in aria-label, not an sr-only span: an sr-only sibling glues onto
               the visible code in plain text extraction ("not assessedA1–A3", there is no whitespace
               node between adjacent spans), and a button names itself from aria-label cleanly. -->
            <button
              v-if="editable"
              v-tooltip="{ text: stageFullName(value), variant: 'label' }"
              type="button"
              :aria-pressed="isIncluded(value)"
              :aria-label="stageAnnouncement(value)"
              :class="[
                ...chipClass(value),
                chipHoverClass(value),
                'focus-ring interactive-active duration-fast ease-standard transition-colors',
              ]"
              @click="emit('toggleStage', value)">
              {{ stageDisplayLabel(value) }}
            </button>

            <!-- Read-only: same visual chip as always for every caller that never passes `editable`. -->
            <span
              v-else
              v-tooltip="{ text: stageFullName(value), variant: 'label' }"
              :class="chipClass(value)">
              <span aria-hidden="true">{{ stageDisplayLabel(value) }}</span>
              <!-- A generic span cannot take aria-label (name-from-author is prohibited on it), so the
                 sentence stays an sr-only sibling. It FOLLOWS the code and leads with ", " so plain
                 text extraction reads "A1–A3, A1 - Raw material supply, …, not assessed" instead of
                 gluing the verdict onto the code ("not assessedA1–A3"), since adjacent spans have no
                 whitespace node between them. The comma separates by itself, so reformatting cannot
                 collapse it away like a bare space. -->
              <span class="sr-only">, {{ stageAnnouncement(value) }}</span>
            </span>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>
